import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetArticleQuery } from '@/services/newsApi';
import { useAppSelector } from '@/store/hooks';
import { ArticleBlocks } from '@/components/news/ArticleBlocks';
import { Loader } from '@/components/common/Loader';
import { formatRelative } from '@/lib/format';

export function ArticleDetail() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation('news');
  const locale = useAppSelector((s) => s.news.locale) || i18n.language || 'en';

  const { data: article, isLoading, isError } = useGetArticleQuery(slug, { skip: !slug });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-danger">
          {t('not_found', { defaultValue: 'Article not found.' })}
        </p>
        <Link to="/news" className="mt-3 inline-block text-sm text-brand">
          ← {t('back_to_list', { defaultValue: 'Back to news' })}
        </Link>
      </div>
    );
  }

  const content = article.content.find((c) => c.locale === locale) ?? article.content[0];
  if (!content) return null;

  return (
    <article className="flex flex-col gap-4 pb-10">
      {article.heroImage?.url && (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-bg-tertiary">
          <img
            src={article.heroImage.url}
            alt={article.heroImage.alt}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      <header className="flex flex-col gap-2 px-4">
        {article.primaryTag && (
          <span className="self-start rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-brand">
            {article.primaryTag.name}
          </span>
        )}
        <h1 className="text-2xl font-bold leading-tight text-fg">{content.title}</h1>
        {content.subtitle && (
          <p className="text-base text-fg-muted">{content.subtitle}</p>
        )}
        <div className="mt-1 flex items-center gap-2 text-xs text-fg-muted">
          <span>{article.authorName}</span>
          <span aria-hidden>·</span>
          <span>{formatRelative(article.publishedAt)}</span>
          <span aria-hidden>·</span>
          <span>{article.readingTimeMinutes} min read</span>
        </div>
      </header>

      <div className="px-4">
        <ArticleBlocks blocks={content.blocks} />
      </div>
    </article>
  );
}
